import React from 'react';
import {
  Eye,
  Palette,
  TableProperties,
  Zap,
  Contrast,
  X,
} from 'lucide-react';
import {
  useAccessibilityStore,
  ColorBlindMode,
} from '../../stores/accessibilityStore';

const COLOR_BLIND_OPTIONS: { value: ColorBlindMode; label: string; desc: string }[] = [
  { value: 'none', label: 'Default', desc: 'Standard color palette' },
  { value: 'deuteranopia', label: 'Deuteranopia', desc: 'Red-green (most common)' },
  { value: 'protanopia', label: 'Protanopia', desc: 'Red-blind' },
  { value: 'tritanopia', label: 'Tritanopia', desc: 'Blue-yellow' },
];

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccessibilityPanel({ isOpen, onClose }: AccessibilityPanelProps) {
  const {
    colorBlindMode,
    usePatternFills,
    reducedMotion,
    highContrast,
    showDataTables,
    setColorBlindMode,
    setUsePatternFills,
    setReducedMotion,
    setHighContrast,
    setShowDataTables,
  } = useAccessibilityStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      {/* Panel - slides in from right */}
      <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Accessibility</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            aria-label="Close accessibility panel"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Color Blind Mode */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-purple-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Color Vision</h3>
            </div>
            <div className="space-y-2">
              {COLOR_BLIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setColorBlindMode(opt.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left ${
                    colorBlindMode === opt.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  aria-pressed={colorBlindMode === opt.value}
                >
                  <div className={`w-3 h-3 rounded-full border-2 ${
                    colorBlindMode === opt.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`} />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Toggle Options */}
          <section className="space-y-3">
            <ToggleOption
              icon={<Palette className="w-4 h-4 text-orange-500" />}
              label="Pattern Fills"
              description="Add patterns to chart bars/segments for non-color distinction"
              checked={usePatternFills}
              onChange={setUsePatternFills}
            />
            <ToggleOption
              icon={<TableProperties className="w-4 h-4 text-green-500" />}
              label="Show Data Tables"
              description="Display data tables below every chart"
              checked={showDataTables}
              onChange={setShowDataTables}
            />
            <ToggleOption
              icon={<Zap className="w-4 h-4 text-yellow-500" />}
              label="Reduced Motion"
              description="Disable chart animations and transitions"
              checked={reducedMotion}
              onChange={setReducedMotion}
            />
            <ToggleOption
              icon={<Contrast className="w-4 h-4 text-gray-700 dark:text-gray-300" />}
              label="High Contrast"
              description="Increase border and text contrast for readability"
              checked={highContrast}
              onChange={setHighContrast}
            />
          </section>

          {/* Color Preview */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Palette Preview</h3>
            <PalettePreview />
          </section>
        </div>
      </div>
    </div>
  );
}

function ToggleOption({
  icon,
  label,
  description,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
      <span className="flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{description}</div>
      </div>
      <div className="flex-shrink-0">
        <div
          role="switch"
          aria-checked={checked}
          className={`w-10 h-5 rounded-full transition-colors relative ${
            checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          onClick={(e) => { e.preventDefault(); onChange(!checked); }}
        >
          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`} />
        </div>
      </div>
    </label>
  );
}

function PalettePreview() {
  const chartColors = useAccessibilityStore((s) => s.getChartColors());
  const statusColors = useAccessibilityStore((s) => s.getStatusColors());

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {chartColors.map((c, i) => (
          <div
            key={i}
            className="flex-1 h-6 rounded"
            style={{ backgroundColor: c }}
            title={`Color ${i + 1}: ${c}`}
          />
        ))}
      </div>
      <div className="flex gap-2 text-[10px]">
        {Object.entries(statusColors).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
            <span className="text-gray-500 dark:text-gray-400 capitalize">{key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
